<template>
  <v-row>
    <v-col cols="12" :lg="showGrossProfit ? 9 : 12" :xl="showGrossProfit ? 10 : 12">
      <v-card flat :min-height="$vuetify.breakpoint.lgAndUp ? 500 : 0" id="contract-activity-table">
        <v-card-title>
          <v-row justify="end" dense>
            <v-col cols="auto">
            <RateChangeDateSelect
              :scheduledRateChanges="scheduledRateChanges"
              :contract="contract"
              @change="onRateChangeDateSelected"
            >
              <template #append-outer>
                <Icon
                v-if="!hasExistingRateChanges"
                icon="mdi-pencil"
                :tooltipText="$t('edit')"
                :small="false"
                @icon-clicked="chooseRateChangeTypeClicked"/>

                <Icon
                v-else
                icon="delete-history"
                :tooltipText="$t('deleteScheduledChanges')"
                :small="false"
                iconColor="error"
                @icon-clicked="deleteRateChangesClicked"/>

                <Icon
                icon="mdi-refresh"
                elementId="refresh"
                :small="false"
                :tooltipText="$t('refresh')"
                @icon-clicked="refreshActivities" />
              </template>
            </RateChangeDateSelect>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text v-if="initialized">
          <ContractDetailActivities
            :contract="contract"
            :entities="activityEntities"
            :allActivities="activitiesToDisplay"
            @refresh-activities="refreshActivities"
            :tract="tract"
            :selectedDate="activityEffectiveDate"
            @scheduled-rate-change-clicked="onScheduledRateChangeIconClicked"
          />
        </v-card-text>

        <v-card-actions v-if="showFinishButton">
          <v-spacer/>
          <v-btn color="primary" @click="$emit('finish-editing')">
            {{ $t('finishEditingContract') }}
          </v-btn>
        </v-card-actions>

        <Dialog
        :stateId="dialogId"
        @close="closeDialog"
        @dialog-closing="closeDialog"
        @click:outside="closeDialog"
        :enforcePersistence="editingRates"
        :confirmClose="immediateRateChangesFormOpen || scheduleRateChangesFormOpen"
        >
          <ContractScheduleRateChangeForm
          v-if="scheduleRateChangesFormOpen"
          :contract="contract"
          @scheduled-rate-change-deleted="onScheduledChangeDeleted"
          @save="onScheduledRateChangeSaved"
          @close="closeDialog"/>

          <ContractRateChangeForm
          v-if="immediateRateChangesFormOpen"
          :contract="contract"
          @save="onImmediateRateChangeSaved"
          @close="closeDialog"/>

          <ActivityRateChangeTypeSelectionCard
          v-if="chooseRateChangeModeDialogOpen"
          :disableSchedule="disableScheduleChanges"
          :contractType="contract.type"
          @continue="onContinueToEditRates">
            <template #schedule-error-message v-if="disableScheduleChanges">
              {{ $t('notAvailableForDraftContracts') }}
            </template>
          </ActivityRateChangeTypeSelectionCard>
        </Dialog>
      </v-card>
    </v-col>

    <v-col cols="12" sm="8" md="6" lg="3" xl="2" v-if="showGrossProfit">
      <v-card flat>
        <v-card-title>
          <span>{{ $t('grossProfitPerTon') }}</span>

          <v-spacer/>

          <Icon
          iconColor="info"
          v-if="showPayByLoadWarning"
          icon="mdi-information-outline"
          :small="false"
          :tooltipText="$t('profitPerTonCannotBeCalculatedForPayByLoadEditing')"/>

          <Icon
          iconColor="info"
          v-if="showNonInventoryWarning"
          icon="mdi-information-outline"
          :small="false"
          :tooltipText="$t('nonInventoryCostsExcluded')"/>
        </v-card-title>

        <v-card-text>
          <v-data-table
          :items="grossProfitByProduct"
          :headers="grossProfitByProductHeaders"
          sort-by="amount"
          sort-desc
          hide-default-footer
          dense>
            <template #item.amount="{item}">
              {{formatMoney(item.amount)}}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ContractType, PayBy, TemplateCostType } from '@/utils/Enumerations.js'
import { profitByProductMap, hasPayByVolumeInventoryActivity } from '@/utils/grossProfit.js'
import { formatMoney } from '@/utils/NumericMutations.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { ActivityRateChangeMode } from '@/components/activity/Activity.js'

export default {
  name: 'Activities',

  props: {
    contract: Object,
    tract: Object,

    showFinishButton: {
      type: Boolean,
      default: false
    }
  },

  components: {
    RateChangeDateSelect: () => import('@/components/activity/RateChangeDateSelect.vue'),
    ContractDetailActivities: () => import('@/components/contract/contract-detail/ContractDetailActivities.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ContractScheduleRateChangeForm: () => import('@/components/contract/scheduled-rate-change/ContractScheduleRateChangeForm.vue'),
    ContractRateChangeForm: () => import('@/components/contract/scheduled-rate-change/ContractRateChangeForm.vue'),
    ActivityRateChangeTypeSelectionCard: () => import('@/components/activity/ActivityRateChangeTypeSelectionCard.vue')
  },

  data: () => ({
    ActivityRateChangeMode,
    creatingDetail: false,
    width: '1000',
    rateStats: undefined,

    dialogId: uniqueDialogId('activity'),
    scheduleRateChangesFormOpen: false,
    immediateRateChangesFormOpen: false,
    initialized: false,
    chooseRateChangeModeDialogOpen: false,
    chosenActivityRateChangeMode: null,
    scheduledRateChanges: [],
    activitiesToDisplay: [],
    activityEntities: [],
    activityEffectiveDate: null,
    editingRates: false
  }),

  computed: {
    ...mapGetters('activity', ['allActivities']),

    grossProfitByProductHeaders () {
      return [
        { text: this.$t('product'), value: 'productName' },
        { text: this.$t('amount'), value: 'amount', align: 'right' }
      ]
    },

    showGrossProfit () {
      return this.contract.type === ContractType.WoodsSale.value && !hasPayByVolumeInventoryActivity(this.allActivities)
    },

    showPayByLoadWarning () {
      return this.allActivities?.flatMap(a => a.activityDetails).some(ad => ad.cost.payBy === PayBy.Load.value)
    },

    showNonInventoryWarning () {
      return this.allActivities?.some(a => a.activityTemplate.costType === TemplateCostType.nonInventoryCost.value)
    },

    grossProfitByProduct () {
      if (this.showPayByLoadWarning) {
        return []
      }

      const map = profitByProductMap(this.allActivities, this.contract.products, this.contract.distance)
      return this.contract.products
        .filter(p => map.has(p.productId))
        .map(p => ({ productName: p.name, productId: p.productId, amount: map.get(p.productId) }))
    },

    hasExistingRateChanges () {
      return this.contract.scheduledRateChangeId > 0
    },

    disableScheduleChanges () {
      return this.contract.isDraft
    }
  },

  async created () {
    try {
      if (this.contract?.tractId) await this.fetchTractContents({ tractId: this.contract.tractId })
      await this.getScheduledRateChanges()
    } finally {
      this.initialized = true
    }
  },

  methods: {
    ...mapActions('activity', ['fetchActivitiesByEntity']),
    ...mapActions('tract-contents', ['fetchTractContents']),
    ...mapActions('activity-templates', ['fetchActivityTemplateStatsById']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('scheduled-rate-change', ['fetchScheduledRateChanges']),

    formatMoney,

    async getScheduledRateChanges () {
      this.scheduledRateChanges = await this.fetchScheduledRateChanges(this.contract.contractId) || []
    },

    async refreshRateStatsById (activityTemplateId) {
      this.rateStats = await this.fetchActivityTemplateStatsById(activityTemplateId)
    },

    async refreshActivities () {
      await this.fetchActivitiesByEntity(this.contract.contractId)
      this.onRateChangeDateSelected({ activities: this.allActivities, entities: this.activityEntities, effectiveDate: this.activityEffectiveDate })
      this.$emit('refreshed')
    },

    openDialog (width = '90vw') {
      this.openOrUpdateDialog({ id: this.dialogId, width })
    },

    closeDialog () {
      this.chooseRateChangeModeDialogOpen = false
      this.chosenActivityRateChangeMode = null
      this.scheduleRateChangesFormOpen = false
      this.immediateRateChangesFormOpen = false
      this.editingRates = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    continueToScheduleRateChanges () {
      this.openDialog()
      this.scheduleRateChangesFormOpen = true
    },

    continueToActivityChanges () {
      this.openDialog()
      this.immediateRateChangesFormOpen = true
    },

    onScheduledChangeDeleted () {
      this.refreshContract()
      this.closeDialog()
    },

    onScheduledRateChangeSaved () {
      this.refreshContract()
      this.closeDialog()
    },

    deleteRateChangesClicked () {
      this.openDialog()
      this.scheduleRateChangesFormOpen = true
    },

    onImmediateRateChangeSaved () {
      this.refreshActivities()
      this.closeDialog()
    },

    refreshContract () {
      this.$emit('refresh-contract')
    },

    onScheduledRateChangeIconClicked () {
      this.scheduleRateChangesFormOpen = true
      this.openDialog()
    },

    chooseRateChangeTypeClicked () {
      this.openDialog(400)
      this.chooseRateChangeModeDialogOpen = true
    },

    onContinueToEditRates (selectedType) {
      this.chooseRateChangeModeDialogOpen = false
      this.chosenActivityRateChangeMode = selectedType
      this.editingRates = true

      switch (selectedType) {
        case ActivityRateChangeMode.Overwrite:
          this.continueToActivityChanges()
          break
        case ActivityRateChangeMode.Schedule:
          this.continueToScheduleRateChanges()
          break
      }
    },

    onRateChangeDateSelected ({ activities, entities, effectiveDate }) {
      this.activitiesToDisplay = activities
      this.activityEntities = entities
      this.activityEffectiveDate = effectiveDate
    }
  }
}
</script>
